import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useSaveDoc } from "../../../shared/hooks/useDoctype";
import {
    useCreateDoctype,
    useUpdateDoctypeData,
} from "../../../shared/hooks/useDocTypesData";

const ItemGroupDialog = ({
    open,
    onClose,
    itemGroup,
    itemGroups,
    defaultData,
    onRefetch,
}) => {
    const [formData, setFormData] = useState({
        item_group_name: "",
        parent_item_group: "",
        is_group: 0,
        showpos: 1,
        description: "",
    });
    const [uploadingImage, setUploadingImage] = useState(false);

    const createItemGroup = useCreateDoctype();
    const updateItemGroup = useUpdateDoctypeData();
    const saveDoc = useSaveDoc();

    useEffect(() => {
        if (itemGroup) {
            // Edit mode
            setFormData({
                item_group_name: itemGroup.item_group_name || "",
                parent_item_group: itemGroup.parent_item_group || "",
                is_group:
                    itemGroup.is_group !== undefined ? itemGroup.is_group : 0,
                showpos:
                    itemGroup.showpos !== undefined ? itemGroup.showpos : 1,
                description: itemGroup.description || "",
            });
        } else {
            // Create mode
            setFormData({
                item_group_name: "",
                parent_item_group: "",
                is_group:
                    defaultData?.is_group !== undefined
                        ? defaultData.is_group
                        : 0,
                showpos: 1,
                description: "",
                ...defaultData, // Apply any other default data
            });
        }
    }, [itemGroup, open]);

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSubmit = async () => {
        try {
            const submitData = {
                ...formData,
                // Ensure parent_item_group is null if empty
                parent_item_group: formData.parent_item_group || null,
            };

            if (itemGroup) {
                // Update existing item group
                await updateItemGroup.mutateAsync({
                    doctype: "Item Group",
                    name: itemGroup.item_group_name,
                    formData: submitData,
                });
                enqueueSnackbar("Item Group updated successfully", {
                    variant: "success",
                });
            } else {
                // Check if group already exists (including those with showpos = 0)
                const existingGroup = itemGroups.find(
                    (group) =>
                        group.item_group_name === submitData.item_group_name
                );

                if (existingGroup && existingGroup.showpos !== 1) {
                    // Group exists but showpos is not 1, update it
                    await updateItemGroup.mutateAsync({
                        doctype: "Item Group",
                        name: existingGroup.item_group_name,
                        formData: {
                            ...existingGroup,
                            ...submitData,
                            showpos: 1,
                        },
                    });
                    enqueueSnackbar("Item Group updated to show in POS", {
                        variant: "success",
                    });
                } else if (!existingGroup) {
                    // Group doesn't exist, create new one
                    await createItemGroup.mutateAsync({
                        doctype: "Item Group",
                        value: {
                            ...submitData,
                            showpos: 1, // Always set showpos to 1 for new groups
                        },
                    });
                    enqueueSnackbar("Item Group created successfully", {
                        variant: "success",
                    });
                } else {
                    // Group already exists with showpos = 1
                    enqueueSnackbar(
                        "Item Group already exists and is visible in POS",
                        { variant: "info" }
                    );
                    onClose();
                    return;
                }
            }

            // Trigger refetch in parent component
            if (onRefetch) {
                onRefetch();
            }

            onClose();
        } catch (error) {
            enqueueSnackbar(
                `Failed to ${itemGroup ? "update" : "create"} item group`,
                { variant: "error" }
            );
        }
    };

    const isFormValid = () => {
        return formData.item_group_name.trim() !== "";
    };

    // Get available parent groups (excluding current group and its children)
    const getAvailableParentGroups = () => {
        // First filter to only include groups with is_group = 1 (parent groups only)
        const parentGroupsOnly = itemGroups.filter(
            (group) => group.is_group === 1
        );

        if (!itemGroup) {
            return parentGroupsOnly; // Only parent groups available for new item group
        }

        // For editing, exclude the current group and its descendants
        const excludeGroups = new Set([itemGroup.item_group_name]);

        // Find all descendants of current group
        const findDescendants = (parentName) => {
            itemGroups
                .filter((group) => group.parent_item_group === parentName)
                .forEach((child) => {
                    excludeGroups.add(child.item_group_name);
                    findDescendants(child.item_group_name);
                });
        };

        findDescendants(itemGroup.item_group_name);

        return parentGroupsOnly.filter(
            (group) => !excludeGroups.has(group.item_group_name)
        );
    };

    const availableParentGroups = getAvailableParentGroups();

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {itemGroup ? "Edit Item Group" : "Create New Item Group"}
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    {!itemGroup && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Item Group Name"
                                value={formData.item_group_name}
                                onChange={(e) =>
                                    handleInputChange(
                                        "item_group_name",
                                        e.target.value
                                    )
                                }
                                required
                            />
                        </Grid>
                    )}

                    {itemGroup && (
                        <Grid item xs={12}>
                            <Box p={2} bgcolor="info.light" borderRadius={1}>
                                <Typography variant="body1" color="info.dark">
                                    <strong>Editing:</strong>{" "}
                                    {itemGroup.item_group_name}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    To rename this group, click on its name in
                                    the list
                                </Typography>
                            </Box>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Autocomplete
                            fullWidth
                            options={[
                                {
                                    item_group_name: "",
                                    label: "None (Root Level)",
                                },
                                ...availableParentGroups.map((group) => ({
                                    ...group,
                                    label: group.item_group_name,
                                })),
                            ]}
                            getOptionLabel={(option) =>
                                option.label || option.item_group_name
                            }
                            value={
                                formData.parent_item_group
                                    ? availableParentGroups.find(
                                          (group) =>
                                              group.item_group_name ===
                                              formData.parent_item_group
                                      ) || null
                                    : {
                                          item_group_name: "",
                                          label: "None (Root Level)",
                                      }
                            }
                            onChange={(event, newValue) => {
                                handleInputChange(
                                    "parent_item_group",
                                    newValue && newValue.item_group_name
                                        ? newValue.item_group_name
                                        : ""
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Parent Item Group"
                                    placeholder="Search and select parent group"
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            multiline
                            rows={3}
                            value={formData.description}
                            onChange={(e) =>
                                handleInputChange("description", e.target.value)
                            }
                        />
                    </Grid>

                    {!defaultData && (
                        <Grid item xs={12} md={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_group === 1}
                                        onChange={(e) =>
                                            handleInputChange(
                                                "is_group",
                                                e.target.checked ? 1 : 0
                                            )
                                        }
                                    />
                                }
                                label="Is Group"
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.showpos === 1}
                                    onChange={(e) =>
                                        handleInputChange(
                                            "showpos",
                                            e.target.checked ? 1 : 0
                                        )
                                    }
                                />
                            }
                            label="Show in POS"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="body2" color="text.secondary">
                                Image Update:
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Click on the group's avatar in the list to
                                update its image
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={
                        !isFormValid() ||
                        createItemGroup.isLoading ||
                        updateItemGroup.isLoading
                    }
                >
                    {createItemGroup.isLoading || updateItemGroup.isLoading
                        ? "Saving..."
                        : itemGroup
                        ? "Update"
                        : "Create"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ItemGroupDialog;
